import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-use';
import { Logo, Drawer, IconButton } from '@c2fo/liquidity';
import { BarsIcon, XmarkIcon } from '@c2fo/liquidity/icons';
import LogoLink from './LogoLink';
import Navigation from './Navigation';
import { useNavigationContext } from './NavigationContext';
import RecentAwardsDrawer from './RecentAwardsDrawer';
import usePortalSync from './usePortalSync';
import UserAvatarMenu from './UserMenu/UserAvatarMenu';

const NavigationMobile = () => {
  const { t } = useTranslation();
  const isMobile = useMedia('(max-width: 768px)');
  const { isMobileNavOpen, setIsMobileNavOpen, closeMobileNav } = useNavigationContext();
  const drawerDialogRef = useRef<HTMLDivElement>(null);
  const shouldShowPortal = usePortalSync(isMobileNavOpen);

  useEffect(() => {
    if (!isMobile && isMobileNavOpen) {
      // blur any focused element inside the drawer before closing to prevent aria-hidden warning
      if (document.activeElement instanceof HTMLElement) {
        const drawer = drawerDialogRef.current;

        if (drawer && drawer.contains(document.activeElement)) {
          document.activeElement.blur();
        }
      }

      closeMobileNav();
    }
  }, [isMobile, isMobileNavOpen, closeMobileNav]);

  const onClick = () => {
    setIsMobileNavOpen(!isMobileNavOpen);
  };

  return (
    <>
      <div className="sticky inset-x-0 bottom-0 z-10 flex items-center justify-between bg-canvas md:hidden">
        <div className="flex h-16 w-full items-center justify-between gap-4 border-b bg-white px-4">
          <div className="flex items-center gap-2">
            <IconButton
              aria-expanded={isMobileNavOpen}
              aria-label={isMobileNavOpen ? t('navigation.closeNavigation') : t('navigation.openNavigation')}
              className="[&>svg]:size-8"
              icon={BarsIcon}
              name={isMobileNavOpen ? t('navigation.closeNavigation') : t('navigation.openNavigation')}
              onClick={onClick}
              variant="ancillary"
            />
            <LogoLink>
              <Logo brandMark className="size-7" />
            </LogoLink>
          </div>
          <div className="flex items-center gap-4">
            <RecentAwardsDrawer variant="mobile" />
            <UserAvatarMenu variant="mobile" />
          </div>
        </div>
      </div>
      <Drawer
        backdropClassName="z-10"
        dialogClassName="z-10 w-full max-w-80"
        hideCloseButton
        isOpen={isMobileNavOpen}
        onClose={closeMobileNav}
        position="left"
        portalContainerRef={drawerDialogRef}
        title={
          <div className="grid w-full grid-cols-1 gap-2">
            <LogoLink>
              <Logo className="h-auto w-20" />
            </LogoLink>
            <div className="absolute right-2 top-2 z-20">
              <IconButton icon={XmarkIcon} name="close" onClick={closeMobileNav} variant="ancillary" />
            </div>
          </div>
        }
      >
        <Navigation
          key={shouldShowPortal ? 'open' : 'closed'}
          className="h-full w-full"
          isAutoWidth
          onItemChange={closeMobileNav}
          portalContainer={drawerDialogRef.current}
          shouldUsePortal={shouldShowPortal}
          variant="mobile"
        />
      </Drawer>
    </>
  );
};

export default NavigationMobile;
